import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SelectedClientCard from "../../Components/SelectedClientCard/SelectedClientCard";
import ConfirmPopup from "../../Components/ConfirmPopup/ConfirmPopup";
import AlertMessage from "../../Components/AlertMessage/AlertMessage";
import InvoiceView from "../Steps/3Inbox/InvoiceView/InvoiceView";
import ArchiveInvoiceView from "../Steps/7Archive/ArchiveInvoiceView/ArchiveInvoiceView";
import { setSelectedInvoice } from "../../Reducer/SelectedInvoiceReducer";
import { compareDuplicateInvoiceApi } from "../../Services/DuplicateService/DuplicateApi";
import {
  SubmissionHistoryItem,
  downloadSubmissionFile,
  getSubmissionHistory,
  restoreSubmission,
} from "../../Services/SubmissionHistoryService/SubmissionHistoryApi";
import { RootState } from "../../store";
import SubHistoryTable, { StatusFilter, SubmissionRow } from "./SubHistoryTable";

const PAGE_SIZE = 50;
const RESTORE_FAILED = "Unable to restore this invoice. Please try again.";
const FILE_UNAVAILABLE = "The invoice file is unavailable.";

const toRow = (item: SubmissionHistoryItem): SubmissionRow => ({ ...item, id: item.invoiceId });

export const SubmissionHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientUserId: string | undefined = useSelector(
    (state: RootState) => state.selectedClient.selectedClient?.userId,
  );

  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [restoreRow, setRestoreRow] = useState<SubmissionRow | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Which existing invoice view Show opened, if any
  const [view, setView] = useState<{ kind: "inbox" | "archive"; invoiceId: number } | null>(null);

  // Only the latest request may update the list, so a slow response for the
  // previously selected client (or an older search) is dropped.
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (pageToLoad: number) => {
      requestRef.current?.abort();
      if (!clientUserId) {
        setRows([]);
        setTotalCount(0);
        return;
      }

      const controller = new AbortController();
      requestRef.current = controller;
      setLoading(true);
      try {
        const result = await getSubmissionHistory({
          clientUserId,
          search: debouncedSearch || undefined,
          status: statusFilter === "all" ? undefined : statusFilter,
          page: pageToLoad,
          limit: PAGE_SIZE,
          signal: controller.signal,
        });
        const items = result.items.map(toRow);
        setRows((prev) => (pageToLoad === 1 ? items : [...prev, ...items]));
        setTotalCount(result.totalCount);
        setPage(pageToLoad);
      } catch (error: any) {
        if (controller.signal.aborted) return;
        console.error("Error loading submission history:", error);
        AlertMessage({ text: "Unable to load submission history.", type: "error" });
      } finally {
        if (requestRef.current === controller) setLoading(false);
      }
    },
    [clientUserId, debouncedSearch, statusFilter],
  );

  // New client, search or filter: start again from the first page
  useEffect(() => {
    setRows([]);
    load(1);
  }, [load]);

  useEffect(() => {
    setView(null);
  }, [clientUserId]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const loadMore = () => {
    if (!loading && rows.length < totalCount) load(page + 1);
  };

  const handleViewImage = (row: SubmissionRow) => {
    if (!row.documentUrl) {
      AlertMessage({ text: FILE_UNAVAILABLE, type: "error" });
      return;
    }
    window.open(row.documentUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (row: SubmissionRow) => {
    if (!clientUserId) return;
    try {
      await downloadSubmissionFile(row, clientUserId);
    } catch (error) {
      console.error("Error downloading invoice file:", error);
      AlertMessage({ text: FILE_UNAVAILABLE, type: "error" });
    }
  };

  const handleShow = (row: SubmissionRow) => {
    sessionStorage.setItem("selectedInvoiceId", row.showInvoiceId.toString());
    dispatch(setSelectedInvoice({ ...row, id: row.showInvoiceId }));
    setView({ kind: row.status === "Archive" ? "archive" : "inbox", invoiceId: row.showInvoiceId });
  };

  const handleReview = async (row: SubmissionRow) => {
    if (row.duplicateFlagId == null) return;
    try {
      const response = await compareDuplicateInvoiceApi(row.duplicateFlagId);
      navigate("/inbox/duplicate-invoice-compare", {
        state: {
          originalInvoice: response,
          duplicateInvoice: response,
          returnTo: "/submission-history",
        },
      });
    } catch (error) {
      console.error("Error opening duplicate comparison:", error);
      AlertMessage({ text: "Unable to open the duplicate comparison.", type: "error" });
    }
  };

  const confirmRestore = async () => {
    if (!restoreRow || !clientUserId) return;
    setRestoring(true);
    try {
      const result = await restoreSubmission(restoreRow.invoiceId, clientUserId);
      // Update the row in place; its position follows the original submission time.
      if (result.item) {
        const updated = toRow(result.item);
        setRows((prev) => prev.map((r) => (r.invoiceId === updated.invoiceId ? updated : r)));
      } else {
        load(1);
      }
      AlertMessage({ text: `Invoice restored to ${result.restoredTo}.`, type: "success" });
    } catch (error) {
      console.error("Error restoring invoice:", error);
      AlertMessage({ text: RESTORE_FAILED, type: "error" });
    } finally {
      setRestoring(false);
      setRestoreRow(null);
    }
  };

  // The invoice views close themselves with setInvoiceView(false)
  const setInvoiceView: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const open = typeof value === "function" ? value(true) : value;
    if (!open) {
      setView(null);
      load(1);
    }
  };

  if (view) {
    const viewProps = {
      setInvoiceView,
      refreshInvoices: () => load(1),
      allInvoices: [],
      sortSettings: {},
      // A one-item list: the views' previous/next navigation stays on this invoice
      fullinvoices: [{ id: view.invoiceId }],
      totalInvoiceCount: 1,
    };
    return view.kind === "archive" ? <ArchiveInvoiceView {...viewProps} /> : <InvoiceView {...viewProps} />;
  }

  const isBlocked = restoreRow?.status === "Blocked duplicate";

  return (
    <div>
      <SelectedClientCard />
      <SubHistoryTable
        data={rows}
        totalCount={totalCount}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onLoadMore={loadMore}
        onRestore={setRestoreRow}
        onShow={handleShow}
        onReview={handleReview}
        onViewImage={handleViewImage}
        onDownload={handleDownload}
      />

      <ConfirmPopup
        isOpen={!!restoreRow}
        title={isBlocked ? "Restore blocked invoice?" : "Restore deleted invoice?"}
        message={
          isBlocked
            ? "This invoice was automatically blocked as a possible duplicate. Restore it to the Inbox?"
            : `Restore this invoice to ${restoreRow?.restoreTarget}, where it was before deletion?`
        }
        confirmLabel="Restore"
        cancelLabel="Cancel"
        confirmDisabled={restoring}
        onConfirm={confirmRestore}
        onCancel={() => !restoring && setRestoreRow(null)}
      />
    </div>
  );
};
